<template>
    <div v-if="isValidControl" :class="[control.containerClass, 'control-view-wrapper', control.additionalContainerClass, {'newly-added': isNewlyAdded, 'has-visibility-rules': hasVisibilityRules}]">

        <!-- Conditional visibility indicator badge -->
        <div v-if="hasVisibilityRules" class="visibility-indicator" :title="visibilityTooltip">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span class="condition-dot">?</span>
        </div>

        <div class="control-view" :class="{'active': isActive}">
            <!-- render the label -->
            <ControlLabel v-show="control.isShowLabel" :control="control" />

            <!-- render the exact field -->
            <component :is="controlComponent"
                       :control="control"
                       :siteID="siteID"
            />
        </div>

        <!-- render the right option to config/drag/... -->
        <ControlOption
            @delete="deleteControl"
            @config="openConfiguration"
            @duplicate="duplicateControl"
            @move="openMoveModal"
            :permissions="permissions"
        />
    </div>
    <!-- Show visible error for unknown control types in builder mode -->
    <div v-else-if="control" class="control-view-wrapper unknown-control-error">
        <div class="unknown-control-placeholder">
            <span class="unknown-control-icon">⚠️</span>
            <span class="unknown-control-text">Unknown control type: "{{ control.type }}"</span>
        </div>
        <!-- Still allow delete so admin can remove the broken control -->
        <ControlOption
            @delete="deleteControl"
            :permissions="permissions"
        />
    </div>
</template>

<script>
    import {STYLE_INJECTION_MIXIN} from "../../mixins/style-injection-mixin";
    import ControlLabel from "../../views/builder/control-views/ControlLabel";
    import {CONTROLS} from "../../configs/controls";
    import ControlOption from "../../views/builder/control-views/ControlOption";
    import {EVENT_CONSTANTS} from "../../configs/events";
    import SidebarRenderer from "../../libraries/sidebar-renderer.class";
    import SidebarControlConfiguration from "../../views/builder/sidebar-config-views/SidebarControlConfiguration";
    import ControlMoveModal from "../../views/builder/modal-config-views/ControlMoveModal";

    export default {
        name: "ControlView",
        components: {ControlOption, ControlLabel},
        mixins: [STYLE_INJECTION_MIXIN],
        props: {
            control: {
                type: Object,
                required: true
            },
            parentId: {
                type: String,
                required: true,
            },
            permissions: Object,
            siteID: String,
            allControls: {
                type: Object,
                default: () => ({})
            },
            allSections: {
                type: Object,
                default: () => ({})
            },
            currentSectionName: {
                type: String,
                default: ''
            }
        },

        data: () => ({
            isActive: false,
            isNewlyAdded: false
        }),

        methods: {
            /**
             * [Emit-from-children] ControlOption will emit this if user want to delete the current control
             */
            deleteControl() {
                // EMIT to FormBuilder to let it delete the control for us
                this.$formEvent.$emit(
                    EVENT_CONSTANTS.BUILDER.CONTROL.DELETE,
                    this.parentId,
                    this.control.uniqueId
                )
            },

            /**
             * [Emit-from-children] ControlOption will emit this if user want to duplicate the current control
             */
            duplicateControl() {
                // EMIT to FormBuilder to clone the control and insert it directly below
                this.$formEvent.$emit(
                    EVENT_CONSTANTS.BUILDER.CONTROL.DUPLICATE,
                    this.parentId,
                    this.control.uniqueId
                )
            },

            /**
             * [Emit-from-children] ControlOption emits this to relocate the control.
             * Opens the GlobalModal; the owning ControlView (matched by runnerId) injects the picker.
             */
            openMoveModal() {
                this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.MODAL.OPEN, this.control.uniqueId)
            },

            /**
             * [Emit-from-GlobalModal] After the modal opens, inject the move picker body.
             * GlobalModal.updateBody only reads { runnerId, component, data }; the ModalRenderer class
             * is a malformed no-op, so we inject a plain object.
             */
            openedMoveModal(runnerId) {
                if (runnerId !== this.control.uniqueId) {
                    return
                }
                this.$formEvent.$emit(
                    EVENT_CONSTANTS.BUILDER.MODAL.INJECT,
                    {
                        runnerId: this.control.uniqueId,
                        component: ControlMoveModal,
                        data: { control: this.control, sourceParentId: this.parentId }
                    },
                    "Move control"
                )
            },

            /**
             * [Emit-from-GlobalModal] Picker chose a destination -> tell FormBuilder to relocate.
             */
            onMoveModalSaveAndClose(runnerId, data) {
                if (runnerId !== this.control.uniqueId || !data) {
                    return
                }
                this.$formEvent.$emit(
                    EVENT_CONSTANTS.BUILDER.CONTROL.MOVE,
                    this.parentId,
                    this.control.uniqueId,
                    data.targetParentId,
                    data.anchor
                )
            },

            /**
             * [Emit-from-children] ControlOption will emit this when user clicked "Cog" button
             * We're opening the sidebar configuration....
             */
            openConfiguration() {
                // If the current one active => no trigger...
                if (this.isActive) {
                    return
                }

                this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.SIDEBAR.OPEN, this.control.uniqueId)
            },

            /**
             * [Emit-from-GlobalSidebar]
             */
            openedConfiguration(runnerId) {
                if (runnerId !== this.control.uniqueId) {
                    return
                }

                // eligible to render sidebar
                this.isActive = true
                this.renderSidebar()
            },

            /**
             * Push an Event to GlobalSidebar to Render the Body :D
             */
            renderSidebar() {
                this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.SIDEBAR.INJECT, new SidebarRenderer(
                    this.control.uniqueId,
                    SidebarControlConfiguration,
                    this.control
                ));
            },

            /**
             * Received closed notification from sidebar
             */
            closedConfiguration() {
                this.isActive = false
            },

            /**
             * Save Control Configuration Data
             * @param {String} runnerId - Control ID
             * @param {Object} controlData - Control Object (After edited in the sidebar)
             */
            saveConfiguration(runnerId, controlData) {
                this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.CONTROL.UPDATE, runnerId, controlData)
            },

            /**
             * Format operator for display in visibility tooltip
             */
            formatOperator(op) {
                const opMap = {
                    'equals': '=',
                    'notEquals': '≠',
                    'in': 'in',
                    'notIn': 'not in',
                    'lt': '<',
                    'lte': '≤',
                    'gt': '>',
                    'gte': '≥'
                }
                return opMap[op] || op || '='
            },

            /**
             * Get human-readable field label from field ID/name, including section name for context
             */
            getFieldLabel(fieldId) {
                if (!fieldId) return '?'

                // Search through allControls to find matching field
                if (this.allControls && typeof this.allControls === 'object') {
                    for (const ctrlId in this.allControls) {
                        const ctrl = this.allControls[ctrlId]
                        // Match by name or uniqueId
                        if (ctrl && (ctrl.name === fieldId || ctrl.uniqueId === fieldId || ctrlId === fieldId)) {
                            const fieldLabel = ctrl.label || ctrl.name || fieldId
                            // Find which section this control belongs to
                            const sectionName = this.findSectionForControl(ctrlId)
                            if (sectionName) {
                                return `${sectionName} > ${fieldLabel}`
                            }
                            return fieldLabel
                        }
                    }
                }

                // If not found, return the original fieldId (might already be a name)
                return fieldId
            },

            /**
             * Find the section name that contains a given control
             */
            findSectionForControl(controlId) {
                if (!this.allSections || typeof this.allSections !== 'object') return ''

                for (const sectionId in this.allSections) {
                    const section = this.allSections[sectionId]
                    if (section && section.controls && Array.isArray(section.controls)) {
                        if (section.controls.includes(controlId)) {
                            return section.headline || section.name || ''
                        }
                    }
                }
                return ''
            }
        },

        computed: {
            /**
             * Check if control is valid and can be rendered
             */
            isValidControl() {
                return this.control && this.control.type && CONTROLS[this.control.type] && CONTROLS[this.control.type].fieldComponent
            },

            /**
             * This accessor will get the component object to let us inject the right control
             */
            controlComponent() {
                // Return null for invalid controls (they won't render, error placeholder shown instead)
                if (!this.isValidControl) {
                    console.warn(`[Builder ControlView] Unknown control type "${this.control?.type}" - showing error placeholder`)
                    return null
                }

                return CONTROLS[this.control.type].fieldComponent
            },

            /**
             * Check if this control has visibility rules configured
             */
            hasVisibilityRules() {
                const rules = this.control && this.control.visibilityRules
                if (!rules || !Array.isArray(rules) || rules.length === 0) return false
                // Check if there are actual conditions defined
                const firstRule = rules[0]
                return firstRule && firstRule.conditions && Array.isArray(firstRule.conditions) && firstRule.conditions.length > 0
            },

            /**
             * Generate tooltip text describing the visibility rules with actual conditions
             */
            visibilityTooltip() {
                if (!this.hasVisibilityRules) return ''
                const rules = this.control.visibilityRules[0]
                const joiner = rules.joiner || 'OR'
                const conditions = rules.conditions || []

                // Build human-readable condition descriptions
                const conditionTexts = conditions.map(cond => {
                    const fieldId = cond.field || '?'
                    // Look up the actual field label/name from allControls
                    const fieldLabel = this.getFieldLabel(fieldId)
                    const op = this.formatOperator(cond.op)
                    const value = cond.value !== undefined ? `"${cond.value}"` : '?'
                    return `${fieldLabel} ${op} ${value}`
                })

                const rulesText = conditionTexts.join(` ${joiner} `)
                return `Show when: ${rulesText}`
            }
        },

        created() {
            // listen to GlobalSidebar
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SIDEBAR.SAVE, this.saveConfiguration)
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SIDEBAR.SAVE_AND_CLOSE, this.saveConfiguration)
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SIDEBAR.AFTER_CLOSED, this.closedConfiguration)
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SIDEBAR.OPENED, this.openedConfiguration)

            // move-to-section modal (GlobalModal): each ControlView injects/relays only its own runnerId
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.MODAL.OPENED, this.openedMoveModal)
            this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.MODAL.SAVE_AND_CLOSE, this.onMoveModalSaveAndClose)
        },

        beforeDestroy() {
            // A move destroys + recreates this control's ControlView. Remove its move-modal listeners
            // from the shared bus so a relocated/destroyed instance can't double-fire on a later move.
            this.$formEvent.$off(EVENT_CONSTANTS.BUILDER.MODAL.OPENED, this.openedMoveModal)
            this.$formEvent.$off(EVENT_CONSTANTS.BUILDER.MODAL.SAVE_AND_CLOSE, this.onMoveModalSaveAndClose)
        },

        mounted() {
            // Highlight newly added controls for better discoverability
            // Only show highlight if control was created within last 2 seconds
            const controlAge = Date.now() - (this.control.createdAt || 0);
            if (this.control.createdAt && controlAge < 2000) {
                this.isNewlyAdded = true;
                // Remove highlight after 4 seconds
                setTimeout(() => {
                    this.isNewlyAdded = false;
                }, 4000);
            }
        },
    }
</script>

<style scoped>
.unknown-control-error {
    border: 2px dashed #dc3545;
    background-color: #fff5f5;
    border-radius: 4px;
}

.unknown-control-placeholder {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #dc3545;
    font-size: 14px;
}

.unknown-control-icon {
    font-size: 18px;
}

.unknown-control-text {
    font-weight: 500;
}
</style>